import * as React from 'react';
import Paper from '@mui/material/Paper';
import { Table as MuiTable, Skeleton } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { TableProps } from '../../types';
import './styles.scss';

export const Table = <T, K extends keyof T>({
  columns = [],
  data = [],
  rowsPerPage = 10,
  count,
  page = 0,
  setPage,
  setRowsPerPage,
  isLoading,
  pageChangeHandler,
  rowsPerPageOptions = [10, 20, 50, 100],
  selectedIds = [], // Add this prop to track selected IDs
  rowKey = 'id' as K, // Add this prop to specify which field to use as row identifier
  type = '',

  stickyColumns,
  stickyColumnWidths,
}: TableProps<T, K>) => {
  const handleChangePage = (
    event: React.MouseEvent | null,
    newPage: number
  ) => {
    setPage(newPage);
    pageChangeHandler && pageChangeHandler(event, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Function to check if a row is selected
  const isRowSelected = (row: T) => {
    return selectedIds.includes(
      row[rowKey as keyof T] as number
    );
  };

  const stickyIdx = Array.isArray(stickyColumns)
    ? stickyColumns
    : stickyColumns !== undefined
    ? [stickyColumns]
    : [];

  // Build a map: columnIndex → { left: number, width: number }
  const stickyMap = new Map<
    number,
    { left: number; width: number }
  >();
  let cumulative = 0;

  stickyIdx.forEach((idx, i) => {
    const width = stickyColumnWidths?.[i] ?? 140; // fallback 140px
    stickyMap.set(idx, { left: cumulative, width });
    cumulative += width;
  });
  return (
    <>
      {!data?.length && !isLoading ? (
        <div className="flex justify-center text-lg font-medium pt-6 no-data-text">
          No Data Found
        </div>
      ) : (
        <Paper
          sx={{
            width: '100%',
            overflow: 'hidden',
            boxShadow: 'none',
            backgroundColor: '#ececec',
          }}>
          <TableContainer
            className="table-wrap-div"
            sx={{ maxHeight: '70vh' }}>
            <MuiTable stickyHeader className="custom-table">
              {/* ---------- HEADER ---------- */}
              <TableHead className="table-head">
                <TableRow className="table-row">
                  {columns?.map((column, idx) => {
                    const sticky = stickyMap.get(idx);
                    return (
                      <TableCell
                        key={column.key as string}
                        align="left"
                        className={`table-cell ${
                          sticky ? 'sticky-col' : ''
                        }`}
                        sx={{
                          ...(sticky && {
                            position: 'sticky',
                            left: sticky.left,
                            zIndex: 12, // header always on top
                            backgroundColor: '#f7f7f7',
                            minWidth: sticky.width,
                            width: sticky.width,
                          }),
                        }}>
                        {column.label}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              {/* ---------- BODY ---------- */}
              <TableBody className="table-body">
                {data.length > 0
                  ? data.map((row, rowIdx) => {
                      const isSelected = isRowSelected(row);
                      const isFullPld =
                        type === 'TimeSeries' &&
                        (row?.pldStatus ===
                          'FULL_PLD_STATUS' ||
                          row?.fieldDetails?.pldStatus ===
                            'FULL_PLD_STATUS');

                      return (
                        <TableRow
                          key={rowIdx}
                          className={`table-row ${
                            isSelected ? 'selected-row' : ''
                          }`}
                          sx={{
                            position: 'relative',
                            zIndex: 0,
                            '&:hover': {
                              backgroundColor: '#EFF5F6',
                              zIndex: 11, // lift whole row on hover
                            },
                            backgroundColor: isSelected
                              ? '#EFF5F6'
                              : '#fff',
                            backgroundImage:
                              !isSelected && isFullPld
                                ? 'linear-gradient(90deg, rgba(253,175,32,0.08) 0%, rgba(255,255,255,0.08) 100%)'
                                : 'none',
                          }}>
                          {columns?.map(
                            (column, colIdx) => {
                              const value =
                                column?.cellModifier
                                  ? column?.cellModifier(
                                      row
                                    )
                                  : (row[
                                      column.key
                                    ] as React.ReactNode);

                              const sticky =
                                stickyMap.get(colIdx);

                              return !isLoading ? (
                                <TableCell
                                  key={colIdx}
                                  align="left"
                                  className={`table-cell ${
                                    sticky
                                      ? 'sticky-col'
                                      : ''
                                  }`}
                                  sx={{
                                    ...(sticky && {
                                      position: 'sticky',
                                      left: sticky.left,
                                      zIndex: 11, // same as hover row
                                      background:
                                        'inherit !important',
                                      minWidth:
                                        sticky.width,
                                      width: sticky.width,
                                      // borderLeft:
                                      //   colIdx === 0 &&
                                      //   !isSelected &&
                                      //   isFullPld
                                      //     ? '3px solid rgba(253,175,32,1)'
                                      //     : '3px solid rgba(0,0,0,0.05)',
                                    }),
                                  }}>
                                  {typeof value ===
                                  'boolean'
                                    ? value
                                      ? 'Active'
                                      : 'Inactive'
                                    : value ?? '-'}
                                </TableCell>
                              ) : (
                                <TableCell
                                  key={colIdx}
                                  align="left"
                                  className={`table-cell ${
                                    sticky
                                      ? 'sticky-col'
                                      : ''
                                  }`}
                                  sx={
                                    sticky
                                      ? {
                                          position:
                                            'sticky',
                                          left: sticky.left,
                                          zIndex: 11,
                                          background:
                                            '#fff',
                                          minWidth:
                                            sticky.width,
                                          width:
                                            sticky.width,
                                        }
                                      : {}
                                  }>
                                  <Skeleton
                                    animation="wave"
                                    variant="text"
                                  />
                                </TableCell>
                              );
                            }
                          )}
                        </TableRow>
                      );
                    })
                  : /* SKELETON ROWS */
                    Array.from({ length: rowsPerPage }).map(
                      (_, rIdx) => (
                        <TableRow
                          key={rIdx}
                          className="table-row">
                          {columns?.map((_, cIdx) => {
                            const sticky =
                              stickyMap.get(cIdx);
                            return (
                              <TableCell
                                key={cIdx}
                                align="left"
                                className={`table-cell ${
                                  sticky ? 'sticky-col' : ''
                                }`}
                                sx={
                                  sticky
                                    ? {
                                        position: 'sticky',
                                        left: sticky.left,
                                        zIndex: 11,
                                        background: '#fff',
                                        minWidth:
                                          sticky.width,
                                        width: sticky.width,
                                      }
                                    : {}
                                }>
                                <Skeleton
                                  animation="wave"
                                  variant="text"
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )
                    )}
              </TableBody>
            </MuiTable>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={count || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
          />
        </Paper>
      )}
    </>
  );
};
